import React, { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Iter61 · Cancel flow with a "save offer" one-shot 25%-off coupon when
 * reason=too_expensive. The offer only appears if (a) the supplier picked
 * "too_expensive" AND (b) they haven't used the coupon before.
 */
export default function CancelPlanModal({ onClose, onConfirm, onSaveOfferAccepted, saveOfferUsed, busy }) {
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [showSaveOffer, setShowSaveOffer] = useState(false);
  const [applyingOffer, setApplyingOffer] = useState(false);

  const tryConfirm = () => {
    if (reason === "too_expensive" && !saveOfferUsed) {
      setShowSaveOffer(true);
      return;
    }
    onConfirm(reason, feedback);
  };

  const acceptSaveOffer = async () => {
    setApplyingOffer(true);
    try {
      await api.post("/payments/apply-save-offer");
      toast.success("Nice — 25% off applied to your next invoice. Your plan stays active.");
      onSaveOfferAccepted?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not apply the save offer");
    } finally {
      setApplyingOffer(false);
    }
  };

  const declineSaveOffer = () => {
    setShowSaveOffer(false);
    onConfirm(reason, feedback);
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="cancel-plan-modal"
    >
      <div className="bg-white max-w-md w-full p-6 shadow-2xl border-l-4 border-[#B35C4A]">
        {!showSaveOffer && (
          <>
            <h3 className="font-serif text-xl text-[#11211A] mb-2">Cancel your subscription</h3>
            <p className="text-xs text-[#4A5550] mb-4 leading-relaxed">
              Your listing stays live and every tier benefit remains until the end of your current billing period. No future charges. You can reactivate any time before then.
            </p>
            <label className="block text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold">Reason for leaving (helps us improve)</label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 border border-[#C4CFC9] text-sm rounded-sm bg-white mb-3"
              data-testid="cancel-reason-select"
            >
              <option value="">— Select a reason —</option>
              <option value="too_expensive">Too expensive</option>
              <option value="no_leads">Not enough RFQs / leads</option>
              <option value="not_ready">Not ready for a directory listing yet</option>
              <option value="found_alternative">Switching to a different platform</option>
              <option value="tech_issues">Technical issues / bugs</option>
              <option value="business_closed">Closing the business</option>
              <option value="other">Other</option>
            </select>
            <label className="block text-xs uppercase tracking-widest text-[#264839] mb-1 font-semibold">Anything else? (optional)</label>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-[#C4CFC9] text-sm rounded-sm bg-white"
              data-testid="cancel-feedback-textarea"
            />
            <div className="mt-5 flex gap-2 justify-end">
              <button onClick={onClose} className="btn-secondary text-xs" data-testid="cancel-plan-close">Never mind</button>
              <button
                onClick={tryConfirm}
                disabled={busy}
                className="text-xs uppercase tracking-widest font-semibold px-4 py-2 bg-[#B35C4A] hover:bg-[#9E4E3E] text-white transition disabled:opacity-50"
                data-testid="cancel-plan-confirm"
              >
                {busy ? "Cancelling…" : "Confirm cancellation"}
              </button>
            </div>
          </>
        )}

        {showSaveOffer && (
          <div data-testid="save-offer-panel">
            <div className="tracking-overline text-[10px] text-[#B35C4A] mb-2">One-time save offer</div>
            <h3 className="font-serif text-2xl text-[#11211A] mb-2">Would 25% off your next month help?</h3>
            <p className="text-sm text-[#4A5550] mb-4 leading-relaxed">
              We hear you on price. Take <strong className="text-[#264839]">25% off your next invoice</strong> — applied automatically as a one-time credit — and keep every category, RFQ credit, and badge you have today.
            </p>
            <div className="border border-[#E8ECE9] p-4 bg-[#FAFAF7] mb-4">
              <div className="flex justify-between items-baseline">
                <span className="text-xs text-[#7D8C85]">Discount</span>
                <span className="font-serif text-2xl text-[#264839] tabular-nums">25% off</span>
              </div>
              <div className="flex justify-between items-baseline mt-1">
                <span className="text-xs text-[#7D8C85]">Duration</span>
                <span className="text-xs text-[#11211A]">Next invoice, one-time</span>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={declineSaveOffer}
                disabled={applyingOffer || busy}
                className="btn-secondary text-xs"
                data-testid="save-offer-decline"
              >
                No thanks, cancel anyway
              </button>
              <button
                onClick={acceptSaveOffer}
                disabled={applyingOffer || busy}
                className="text-xs uppercase tracking-widest font-semibold px-4 py-2 bg-[#264839] hover:bg-[#1B3529] text-white transition disabled:opacity-50"
                data-testid="save-offer-accept"
              >
                {applyingOffer ? "Applying…" : "Claim 25% off"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
